import React, { forwardRef, Suspense } from 'react';

import type { RicosEditorProps } from './../ricos-common';
import { isSSR } from './../wix-rich-content-common';
import { DevContextProvider, shouldShowDebuggingInfo } from './../wix-rich-content-common/lib/devTools';
import {
  hasWixInsiderOverride,
  OverrideFullRicosEditorLazy,
  OverridePluginsResolver,
} from './../wix-rich-content-common/lib/overrides';
import { isiOS } from './../editor-common';

import { loadCSS } from './load-css';
import type { RicosEditorRef } from './RicosEditorRef';
import { RicosErrorBoundary } from './tiptap/RicosErrorBoundary';

const FullRicosEditorLazy = React.lazy(() => {
  loadCSS();
  return import(/* webpackChunkName: "ricos_FullRicosEditor" */ './tiptap/FullRicosEditor');
});

const LocaleResourceProviderLazy = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ricos_LocaleResourceProvider" */ './RicosContext/locale-resource-provider'
    )
);

const renderMobileDevTools = () => {
  // @ts-ignore-next-line
  if (typeof window !== 'undefined' && !window.__RICOS_DEVTOOLS_MOBILE_HOOK__) {
    // @ts-ignore-next-line
    window.__RICOS_DEVTOOLS_MOBILE_HOOK__ = true;
    const cdnTag = document.createElement('script');
    cdnTag.src = '//cdn.jsdelivr.net/npm/eruda';
    cdnTag.onload = () => {
      // @ts-ignore-next-line
      eruda.init();
    };
    window.document.head.appendChild(cdnTag);
  }
};

export const RicosEditor = forwardRef<RicosEditorRef, RicosEditorProps & { jsdomMode?: boolean }>(
  (props, ref) => {
    if (
      (props.debugMode?.includes('mobile') || props.debugMode?.includes('all')) &&
      props.isMobile
    ) {
      renderMobileDevTools();
    }

    const loadingPlaceholder = props.loadingPlaceholder || <div />;

    const Wrapper = shouldShowDebuggingInfo(props.debugMode?.includes('all'))
      ? DevContextProvider
      : React.Fragment;

    const FullRicosEditorComp = hasWixInsiderOverride
      ? OverrideFullRicosEditorLazy
      : FullRicosEditorLazy;

    return isSSR() ? (
      <div />
    ) : (
      <Wrapper>
        <RicosErrorBoundary onError={props.onError}>
          <Suspense fallback={loadingPlaceholder}>
            <LocaleResourceProviderLazy fallback={loadingPlaceholder} locale={props.locale}>
              {locale => t =>
                (
                  <OverridePluginsResolver
                    hasOverride={hasWixInsiderOverride}
                    plugins={props.plugins}
                  >
                    {plugins => (
                      <FullRicosEditorComp
                        {...props}
                        plugins={plugins}
                        locale={locale}
                        t={t}
                        ref={ref}
                        platform={isiOS() ? 'macOs' : 'windows'}
                      />
                    )}
                  </OverridePluginsResolver>
                )}
            </LocaleResourceProviderLazy>
          </Suspense>
        </RicosErrorBoundary>
      </Wrapper>
    );
  }
);
RicosEditor.displayName = 'RicosEditor';
